import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/utils/shadcn/utils'

import { ButtonSize, ButtonSizePx } from './variants'

const buttonVariants = cva(
  `
  inline-flex items-center justify-center whitespace-nowrap rounded-md gap-2 transition-colors ease 
  border-[1px]
  transition-width
  duration-300
  hover:cursor-pointer
  disabled:cursor-not-allowed
  `,
  {
    variants: {
      variant: {
        primary: `
          bg-moment-teal-900 
          dark:bg-moment-teal-800
          border-moment-teal-800
          dark:border-moment-teal-700
          text-moment-white-100 
          hover:bg-moment-teal-1000
          dark:hover:bg-moment-teal-700
          disabled:bg-moment-teal-200
          disabled:text-moment-teal-400
          disabled:border-moment-teal-200
          dark:disabled:border-moment-teal-1000
          dark:disabled:bg-moment-teal-1000
          dark:disabled:text-moment-teal-900
          `,
        secondary: `
          bg-moment-white-100
          dark:bg-moment-blue-grey-1000
          border-moment-blue-grey-200
          dark:border-moment-blue-grey-900
          text-foreground
          hover:bg-moment-white-200
          dark:hover:bg-moment-blue-grey-900
          dark:hover:border-moment-blue-grey-900
          disabled:border-moment-blue-grey-200
          disabled:bg-moment-white-100
          disabled:text-moment-blue-grey-400
          dark:disabled:border-moment-blue-grey-1000
          dark:disabled:bg-moment-blue-grey-1000
          dark:disabled:text-moment-blue-grey-700
        `,
        danger: `
          bg-moment-red-700 
          border-moment-red-600 
          text-moment-white-100 
          hover:bg-moment-red-800 
          dark:hover:border-moment-red-500 
          dark:hover:bg-moment-red-600 
          disabled:bg-moment-red-200
          disabled:border-moment-red-200
          disabled:text-moment-red-100
          dark:disabled:border-moment-red-1000
          dark:disabled:bg-moment-red-1000
          dark:disabled:text-moment-red-900
        `,
        ghost: `
          bg-transparent
          border-none
        `,
        badge: `
          bg-moment-teal-200
          border-moment-teal-300
          text-moment-blue-grey-800
          hover:bg-moment-teal-300
          hover:border-moment-teal-400
          dark:bg-moment-blue-grey-900
          dark:border-moment-blue-grey-800
          dark:text-moment-grey-300
          dark:hover:bg-moment-blue-grey-800
          dark:hover:border-moment-blue-grey-700
          disabled:bg-moment-teal-100
          disabled:border-moment-teal-200
          disabled:text-moment-blue-grey-400
          dark:disabled:bg-moment-blue-grey-1000
          dark:disabled:border-moment-blue-grey-900
          dark:disabled:text-moment-blue-grey-700
        `,
      },
      size: {
        [ButtonSize.XSmall]: `h-[${ButtonSizePx.XSmall}px] rounded-md px-2 py-0`,
        [ButtonSize.Small]: `h-[${ButtonSizePx.Small}px] rounded-md px-2.5 py-0.5`,
        [ButtonSize.Large]: `h-[${ButtonSizePx.Large}px] rounded-md px-3 py-1`,
        badge: `h-auto rounded-[6px] px-2 py-0.5`,
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: ButtonSize.Small,
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button }
